<template>
  <div class="rate-policy">
    <div name="differencials-and-rates">
      <!-- SELEÇÃO DE DIFERENCIAIS -->
      <b-row class="form--row">
        <PmwFormSelect
          v-model="rateDifferencials"
          :options="rateItensOptions"
          :search="true"
          :multiple="true"
          label="O que diferencia essa flutuação"
          placeholder="Selecione os diferenciais"
          class="form--input form--input__select"
          @input="selectDifferentialHandler"
        />
      </b-row>

      <b-row class="form--title">
        <b-col class="form--title__size">
          <PmwHeading
            size="paragraph-60"
            weight="weight-medium"
            class="form--title__text"
          >
            Descreva brevemente o que inclui a tarifa
          </PmwHeading>

          <b-row class="form--row mb-2">
            <Textarea
              :value="descriptionTextValue"
              :counter="true"
              :maxlength="1500"
              :hasLanguages="true"
              :language="language"
              :languageCodes="{ pt: 'pt-BR', en: 'en-US', es: 'es-ES' }"
              label=""
              placeholder="Deixando em branco, serão exibidas informações padrão do hotel."
              class="form--textarea"
              @changeLanguage="language = $event"
              @input="descriptionTextHandler"
            />
          </b-row>

          <PmwParagraph class="form--subtitle__not-visible" size="paragraph-40">
            Deixando este campo sem preencher, utilizaremos os dados do Cadastro
            do Hotel. Caso queira uma descrição diferente, preencha o campo
            acima.
          </PmwParagraph>
        </b-col>

        <b-col class="form--title__size">
          <PmwHeading
            size="paragraph-60"
            weight="weight-medium"
            class="form--title__text"
          >
            Política e restrições específicas da tarifa
          </PmwHeading>

          <b-row class="form--row mb-2">
            <Textarea
              :value="politicsTextValue"
              :counter="true"
              :maxlength="1500"
              :hasLanguages="true"
              :language="language"
              :languageCodes="{ pt: 'pt-BR', en: 'en-US', es: 'es-ES' }"
              label=""
              placeholder="Deixando em branco, serão exibidas informações padrão do hotel."
              class="form--textarea"
              @changeLanguage="language = $event"
              @input="politicsTextHandler"
            />
          </b-row>

          <PmwParagraph class="form--subtitle" size="paragraph-40">
            Deixando este campo sem preencher, utilizaremos os dados do Cadastro
            do Hotel. Caso queira uma descrição diferente, preencha o campo
            acima.
          </PmwParagraph>
        </b-col>
      </b-row>
    </div>

    <div name="billing-payment-methods">
      <BillingPaymentMethods
        :fluctuationData="fluctuationData"
        :hotelPaymentForms="hotelPaymentForms"
        :hotelPaymentType="hotelData?.tipoPagamento"
        :pixRate="hotelData?.habilitarPagamentoPix"
        :blockPixEdition="hotelData?.pixHabilitadoTodasTarifas"
        :hidePrePayment="
          hotelData?.gatewayPagamento !== 'MUNDIPAGG' &&
            !hotelData?.habilitarPagamentoPix
        "
        @load-payment-forms="$emit('load-payment-forms')"
      />
    </div>
  </div>
</template>

<script>
import PmwHeading from '@solucoes/components-lib-tmp/src/components/elements/PmwHeading'
import Textarea from '@solucoes/components-lib-tmp/src/components/elements/Textarea'
import PmwFormSelect from '@solucoes/components-lib-tmp/src/components/patterns/PmwFormSelect'
import PmwParagraph from '@solucoes/components-lib-tmp/src/components/elements/PmwParagraph'

import BillingPaymentMethods from './BillingPaymentMethods'

export default {
  name: 'RatePolicy',

  components: {
    PmwHeading,
    PmwFormSelect,
    Textarea,
    BillingPaymentMethods,
    PmwParagraph
  },

  props: {
    fluctuationData: Object,

    rateItensOptions: {
      type: Array,
      default: () => []
    },

    hotelPaymentForms: {
      type: Array,
      default: () => []
    },

    hotelData: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      /** Linguagem selecionada */
      language: 'pt-BR',

      /** valor interno para controle dos diferenciais */
      rateDifferencials: []
    }
  },

  computed: {
    descriptionTextValue() {
      /** Retorna a descricao do idioma selecionado */
      return (
        this.fluctuationData.idiomas.find(
          (item) => item.idioma === this.language
        )?.pensao || ''
      )
    },

    politicsTextValue() {
      /** Retorna o texto das politicas e restricoes do idioma selecionado */
      return (
        this.fluctuationData.idiomas.find(
          (item) => item.idioma === this.language
        )?.politica || ''
      )
    }
  },

  watch: {
    rateItensOptions(newValue) {
      this.rateDifferencials = newValue.filter(
        (item) =>
          this.fluctuationData.destaques?.findIndex(
            (fItem) => +fItem.codigo === +item.value
          ) > -1
      )
    }
  },

  created() {
    if (this.fluctuationData.destaques?.length) {
      this.rateDifferencials = this.fluctuationData.destaques.map((item) => ({
        value: item.destaque,
        text: item.codigo
      }))
    }

    this.$differentials.get(this.$route.params.id).then((response) => {
      this.rateDifferencialsOptions = [] || response.data
    })
  },

  methods: {
    selectDifferentialHandler($event) {
      this.fluctuationData.destaques = $event.map((item) => {
        return {
          codigo: item.value,
          destaque: false
        }
      })
    },

    /**
     * Manipula a mudança de valores da descriçao
     * @param $event - Texto do texarea
     * */
    descriptionTextHandler($event) {
      const langMatch = this.fluctuationData.idiomas.find(
        (item) => item.idioma === this.language
      )

      if (langMatch) {
        this.$set(langMatch, 'pensao', $event)
      } else {
        this.fluctuationData.idiomas.push({
          idioma: this.language,
          pensao: $event
        })
      }
    },

    /** Manipula a mudança de valores da descriçao
     * @param $event - Texto do texarea
     */
    politicsTextHandler($event) {
      const langMatch = this.fluctuationData.idiomas.find(
        (item) => item.idioma === this.language
      )

      if (langMatch) {
        this.$set(langMatch, 'politica', $event)
      } else {
        this.fluctuationData.idiomas.push({
          idioma: this.language,
          politica: $event
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.rate-policy {
  min-width: 900px;
  .form {
    &--title {
      margin-bottom: 24px;
      min-width: auto;
      display: inline-flex;
      gap: 40px;
      display: flex;

      &__spacing {
        margin-top: 16px;
      }

      &__text {
        margin-bottom: 4px;
        font-size: font-size('size-medium');
      }
    }

    &--subtitle {
      font-size: font-size('size-small');
      font-weight: font-weight('weight-normal');
      color: pmw-colors(grey-80);
      line-height: 21px;

      &__not-visible {
        visibility: hidden;
        font-size: font-size('size-small');
        font-weight: font-weight('weight-normal');
        color: pmw-colors(grey-80);
        line-height: 21px;
        margin-top: 8px;
      }

      &__link {
        color: pmw-colors(blue-100);
        max-width: 150px;
        line-height: 18px;
        cursor: pointer;
        text-decoration: underline;

        &:hover {
          color: pmw-colors(blue-80);
        }
      }

      @media (max-width: 1366px) {
        max-width: 400px;
      }
    }

    &--textarea {
      ::v-deep {
        .pmw-textarea--field {
          line-height: 18px;
        }

        .pmw-textarea--label {
          visibility: hidden;
        }
      }

      @media (max-width: 1366px) {
        max-width: 400px;
      }
    }

    &--input {
      /** Alterar cores novas do select */
      ::v-deep {
        .pmw-select--button__caret-sort-up,
        .pmw-select--button__show {
          border-bottom-color: pmw-colors('blue-100');
        }
        .pmw-select--button:focus {
          border-color: pmw-colors('blue-100');
          border-bottom-color: pmw-colors('blue-100');
        }
        .pmw-select-options--item__hover:hover,
        .pmw-select-options--item__checked {
          background-color: pmw-colors('blue-100') !important;
        }
        .pmw-form-field--label {
          font-weight: font-weight('weight-medium');
        }
        .form-field--label-wrapper {
          line-height: 21px;
          margin-bottom: 3px;
        }
        .pmw-input {
          background: $white;
        }
        .pmw-input--clear {
          z-index: 0;
        }
        .pmw-input__focus {
          border-color: $pmw-blue;
        }

        .pmw-input--field {
          background: $pmw-white;

          &:focus {
            border-color: $pmw-blue;
          }
        }

        .pmw-input__disabled {
          input {
            background: pmw-colors('grey-20');
            color: $pmw-gray-darker;
          }
        }

        .pmw-custom-tooltip {
          line-height: 18px;
        }
      }

      &__select {
        max-width: 350px;

        &.error {
          ::v-deep {
            .pmw-select--button {
              border-bottom-color: pmw-colors('error-100');
              border-bottom-width: 2px;

              span {
                width: 100%;
                overflow: hidden;
                text-overflow: ellipsis;
                display: inline-block;
                white-space: nowrap;
              }
            }
          }
        }
      }
    }

    &--row {
      display: flex;
      align-items: flex-end;
      flex-wrap: wrap;
      gap: 28px;
      margin-bottom: 24px;
    }
  }
}
</style>
